/**
 * `ref.wrong_type` (§5.2 Reference integrity) — a curated predicate's
 * `ref_types` (declared `ref_root` / `ref_roots`) excludes the target
 * entity's root. The referent EXISTS (that's `ref.dangling`'s job); it's just
 * the wrong kind of thing.
 *
 * The referent may be a bare `ref` value OR the `parent` embedded in a
 * `containment` value (#206 — `contained_in` keeps `ref_root: location`, so a
 * non-Location parent still warns). `factRefTarget` resolves both shapes.
 *
 * Severity: `warning`. The auditor-advises principle — the GM may override;
 * the fact persists and the edge counts as valid in graph queries
 * (`03a-entities.md §6`, mirrored by `diagnoseRef`).
 */
import { currentFactValue, diagnoseRef, factRefTarget } from '@dl/shared';

import type { RawAdvisory } from '../advisory.js';
import { factRef } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { forEachFact } from './helpers.js';

export function refWrongType(ctx: AuditContext): RawAdvisory[] {
  const out: RawAdvisory[] = [];

  forEachFact(ctx.entities, ({ entity, factKey, predicate, fact }) => {
    const decl = ctx.registry.lookup(predicate);
    if (!decl) return;

    const value = currentFactValue(fact);
    const targetId = factRefTarget(decl, value);
    if (targetId === null) return;

    const target = ctx.entities[targetId];
    if (target === undefined) return; // dangling — a different rule owns this

    const diagnostic = diagnoseRef(decl, target.root);
    if (diagnostic === null) return; // root is acceptable (or unconstrained)

    out.push({
      severity: 'warning',
      rule: 'ref.wrong_type',
      message_key: 'auditor.ref.wrong_type',
      params: {
        predicate,
        target: targetId,
        actual: diagnostic.actual,
        expected: diagnostic.expected.join(', '),
      },
      fact_ids: [factRef(entity.id, factKey)],
      entity_ids: [entity.id, targetId],
    });
  });

  return out;
}
